import React, { useState, useEffect } from "react";
import * as yup from "yup";
import SoHeader from "./soHeader";
import { Accordion, AccordionTab } from "primereact/accordion";
import Swal from "sweetalert2";
import ContentLayout from "../../../../../partials/content/ContentLayout";
import {
  saveSalesOrder,
  getSalesOrder,
} from "../../../../../services/sales/salesOrderService";
import { getCustomerAddress } from "../../../../../services/sales/customerService";
import { PageSubHeader } from "../../../../../../provider/PageDataProvider";
import SalesItem from "../../sales-item/salesItem";
import AppForm from "../../../../../partials/content/Forms/AppForm";
import { useGlobalData } from "../../../../../../provider/GlobalDataProvider";
import { sendApprovalRequests, setObject } from "../../../../../utils/utils";
import store from "../../../../../store/store";
import { DateTime } from "luxon";
const salesOrderFormSchema = () => {
  return yup.object().shape({
    ID: yup.number(),
    customerPoNo: yup
      .string()
      .max(255, "Customer PO Number maximum characters is 255"),
    orderDate: yup
      .date()
      .required("Order Date is required")
      .max(
        yup.ref("expDeliveryDate"),
        "Order Date must be less equal to Expected Delivery Date"
      ),
    expDeliveryDate: yup
      .date()
      .required("Expected Delivery Date is required")
      .min(
        yup.ref("orderDate"),
        "Expected Delivery Date must be greater equal to Order Date"
      ),
    salesID: yup.number(),
    customerID: yup.number().required(),
    customerName: yup.string().required("Customer Name is required"),
    currencyID: yup.number(),
    currencyCode: yup.string().required("Currency is required"),
    subTotal: yup.number().required("Please add item to add amount"),
    grandTotal: yup.number().required("Please add item to add amount"),
    note: yup.string(),
    toc: yup.string(),
    soBillingAddress: yup.object().shape({
      address: yup
        .string()
        .required("Address is required")
        .max(255, "Address maximum characters is 255"),
      city: yup
        .string()
        .required("City is required")
        .max(50, "City maximum characters is 50"),
      addressPhone: yup
        .string()
        .matches(
          /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,6}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/,
          "Phone Number must be valid. e.g. +6281XXXXXXX or 081XXXXXXX"
        )
        .max(50, "Phone Number maximum characters is 50"),
      addressEmail: yup
        .string()
        .email("Email must be valid. e.g. joe@invifo.com")
        .max(50, "Email maximum characters is 50"),
      province: yup.string().max(50, "Province maximum characters is 50"),
      countryCode: yup.string(),
      postalCode: yup.string().max(10, "Postal Code maximum characters is 10"),
    }),
    soDeliveryAddress: yup.object().shape({
      address: yup
        .string()
        .required("Address is required")
        .max(255, "Address maximum characters is 255"),
      city: yup
        .string()
        .required("City is required")
        .max(50, "City maximum characters is 50"),
      addressPhone: yup
        .string()
        .matches(
          /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,6}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/,
          "Phone Number must be valid. e.g. +6281XXXXXXX or 081XXXXXXX"
        )
        .max(50, "Phone Number maximum characters is 50"),
      addressEmail: yup
        .string()
        .email("Email must be valid. e.g. joe@invifo.com")
        .max(50, "Email maximum characters is 50"),
      province: yup.string().max(50, "Province maximum characters is 50"),
      countryCode: yup.string(),
      postalCode: yup.string().max(10, "Postal Code maximum characters is 10"),
    }),
    salesOrderItem: yup
      .array()
      .of(
        yup.object().shape({
          ID: yup.number(),
          childItem: yup.bool(),
          itemID: yup.number(),
          varCode: yup.string(),
          itemName: yup.string().required("Item is required"),
          qty: yup
            .number()
            .transform((value) => (isNaN(value) ? 0 : value))
            .nullable()
            .required("Quantity is required")
            .moreThan(0, "Quantity cannot be zero or empty")
            .default(0),
          uomID: yup.number().default(0),
          uomName: yup.string().when("itemID", {
            is: (val) => val > 0,
            then: yup.string().required("Unit is required"),
          }),
          unitPrice: yup
            .number()
            .transform((value) => (isNaN(value) ? 0 : value))
            .nullable()
            /*
            .required("Unit Price is required")
            .moreThan(0, "Unit Price cannot be zero or empty")
            */
            .when("childItem", {
              is: false,
              then: yup
                .number()
                .moreThan(0, "Unit Price cannot be zero or empty"),
            })
            .default(0),
          discountPercent: yup.number().default(0),
          discountAmount: yup
            .number()
            .transform((value) => (isNaN(value) ? 0 : value))
            .nullable()
            .default(0),
          taxPercent: yup.number().default(0),
          taxAmount: yup
            .number()
            .transform((value) => (isNaN(value) ? 0 : value))
            .nullable()
            .default(0),
          totalPrice: yup
            .number()
            .transform((value) => (isNaN(value) ? 0 : value))
            .nullable()
            .required("Total Price is required")
            .default(0),
          deleteInd: yup.boolean(),
        })
      )
      .required("Sales Order Items is required"),
    salesOrderAddCost: yup.array().of(
      yup.object().shape({
        ID: yup.number(),
        itemName: yup.string().required("Cost Name is Required"),
        costPrice: yup
          .number()
          .transform((value) => (isNaN(value) ? 0 : value))
          .nullable()
          .required("Cost Price is required")
          .moreThan(0, "Cost Price cannot be zero or empty")
          .default(0),
      })
    ),
  });
};

function SalesOrderForm({ match, history }) {
  const { companyProfile, socket } = useGlobalData();
  const { auth } = store.getState();
  const { fullName } = auth.user;
  const [so, setSo] = useState({
    ID: 0,
    salesOrderCode: "",
    soType: 1,
    operationTitle: "",
    operationTypeID: 0,
    operationTypeName: "",
    operationStatusID: 0,
    operationStatusName: "",
    operationDescription: "",
    customerPoNo: "",
    giStatus: 1,
    giStatusDesc: "",
    reserveStatus: 1,
    invoiceStatus: 1,
    invoiceStatusDesc: "",
    orderDate: DateTime.now().startOf("day"),
    expDeliveryDate: DateTime.now()
      .endOf("day")
      .plus({ days: 7 }),
    salesID: 0,
    salesName: "",
    discountID: 0,
    discountName: "",
    customerID: 0,
    customerName: "",
    soBillingAddressID: 0,
    soBillingAddress: {
      addressID: 0,
      address: "",
      city: "",
      province: "",
      countryCode: companyProfile.countryCode,
      postalCode: "",
      addressPhone: "",
      addressEmail: "",
    },
    soDeliveryAddressID: 0,
    soDeliveryAddress: {
      addressID: 0,
      address: "",
      city: "",
      province: "",
      countryCode: companyProfile.countryCode,
      postalCode: "",
      addressPhone: "",
      addressEmail: "",
    },
    currencyID: companyProfile.currency.ID,
    currencyCode: companyProfile.currency.currencyCode,
    exchangeRate: 1,
    subTotal: 0,
    discountPercent: 0,
    discountAmount: 0,
    taxPercent: 0,
    taxAmount: 0,
    taxName: "",
    addCostTotal: 0,
    grandTotal: 0,
    localAmount: 0,
    note: "",
    tocName: {},
    toc: "",
    approvalStatus: 0,
    approvalStatusDesc: "",
    salesOrderItem: [],
    salesOrderAddCost: [],
    CreatedAt: DateTime.now(),
    UpdatedAt: DateTime.now(),
    createdBy: 0,
    updatedBy: 0,
  });
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const { salesOrderID = 0 } = match.params;
  useEffect(() => {
    const { auth } = store.getState();

    if (salesOrderID > 0) {
      setObject(socket, "lock", salesOrderID, "SORD", auth.user)
        .then((response) => {
          getSalesOrderData(salesOrderID);
        })
        .catch((error) => {
          history.push("/sales/sales-order/sales-order-list");
        });
    }
    return () => {
      if (salesOrderID > 0) {
        setObject(socket, "release", salesOrderID, "SORD", auth.user);
      }
    };
  }, [salesOrderID]);
  return (
    <React.Fragment>
      <PageSubHeader
        title="Sales Order Form"
        breadcrumbs={[
          { page: "/menu", title: "Menu", active: false },
          {
            page: "/sales/sales-order/sales-order-list",
            title: "Sales Orders",
            active: false,
          },
          {
            page: "/sales/sales-order/sales-order-form",
            title: "Sales Order Form",
            active: true,
          },
        ]}
        actionButtons={[
          {
            title: "",
            color: "label-brand",
            icon: "fas fa-backward",
            onClick: () => {
              history.push("/sales/sales-order/sales-order-list");
            },
          },
        ]}
      />
      <AppForm
        initialValues={so}
        validationSchema={salesOrderFormSchema}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          values.subTotal = parseFloat(values.subTotal);
          values.grandTotal = parseFloat(values.grandTotal);
          values.discountAmount = parseFloat(values.discountAmount);
          values.taxAmount = parseFloat(values.taxAmount);

          values.salesOrderItem.forEach((item) => {
            item.unitPrice = parseFloat(item.unitPrice);
            item.qty = parseFloat(item.qty);
            item.totalPrice = parseFloat(item.totalPrice);
            item.discountAmount = parseFloat(item.discountAmount);
            item.taxAmount = parseFloat(item.taxAmount);
            item.discountPercent = parseFloat(item.discountPercent);
            item.taxPercent = parseFloat(item.taxPercent);
          });
          Swal.mixin({
            customClass: {
              confirmButton: "btn btn-success btn-md btn-pill btn-bold",
              cancelButton: "btn btn-danger btn-md btn-pill btn-bold",
            },
            buttonsStyling: false,
          })
            .fire({
              title: "Are you sure want to save this Sales Order ?",
              text: "Please note that you are responsible for this action",
              icon: "question",
              showCloseButton: true,
              showCancelButton: true,
              confirmButtonText: `<i class="fas fa-check-circle"></i> Yes, save it`,
              cancelButtonText: `<i class="fas fa-times-circle"></i> No, cancel`,
              reverseButtons: true,
            })
            .then((result) => {
              if (result.isConfirmed) {
                saveSalesOrder(values).then((response) => {
                  Swal.mixin({
                    customClass: {
                      confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                    },
                    buttonsStyling: false,
                  }).fire({
                    title: "Saved",
                    html: response.data.message,
                    icon: "success",
                    showCloseButton: true,
                    confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
                  });

                  let toUsers = [];
                  const { data } = response.data;

                  if (
                    data.notificationToUsers &&
                    data.notificationToUsers.length > 0
                  ) {
                    data.notificationToUsers.map(
                      (notificationToUser, index) => {
                        toUsers.push({
                          ID: notificationToUser.toUserID,
                        });
                      }
                    );
                  }

                  if (toUsers && toUsers.length > 0) {
                    sendApprovalRequests(socket, fullName, toUsers);
                  }

                  history.push("/sales/sales-order/sales-order-list");
                });
              } else if (result.dismiss === Swal.DismissReason.cancel) {
                Swal.mixin({
                  customClass: {
                    confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                  },
                  buttonsStyling: false,
                }).fire({
                  title: "Cancelled",
                  text: "No change has been made",
                  icon: "info",
                  showCloseButton: true,
                  confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
                });
              }
            });
        }}
        headSubmitButton={true}
      >
        <ContentLayout>
          <SoHeader
            customerAddresses={customerAddresses}
            onChangeCustomer={handleChangeCustomer}
          />
          <div className="accordion">
            <div className="card">
              <Accordion
                className="accordion"
                activeIndex={activeIndex}
                onTabChange={(e) => setActiveIndex(e.index)}
              >
                <AccordionTab
                  header={
                    <React.Fragment>
                      <i className="fas fa-sitemap"></i>
                      <span>
                        <strong>Sales Order Items</strong>
                      </span>
                    </React.Fragment>
                  }
                >
                  <div className="row">
                    <div className="col-sm-12 col-md-12 col-lg-12">
                      <SalesItem
                        property="salesOrderItem"
                        propertyAddCost="salesOrderAddCost"
                      />
                    </div>
                  </div>
                </AccordionTab>
              </Accordion>
            </div>
          </div>
        </ContentLayout>
      </AppForm>
    </React.Fragment>
  );

  async function handleChangeCustomer(customerID = 0) {
    const responseAddress = await getCustomerAddress(customerID);
    const { data: dataAddress } = responseAddress.data;
    setCustomerAddresses(dataAddress.addresses);
  }

  async function getSalesOrderData(salesOrderID = 0) {
    if (salesOrderID > 0) {
      const responseSO = await getSalesOrder(salesOrderID);
      const { data } = responseSO.data;
      let soData = data.salesOrder;
      const soValues = {
        ID: soData.ID,
        salesOrderCode: soData.salesOrderCode,
        soType: soData.soType,
        customerPoNo: soData.customerPoNo,
        giStatus: soData.giStatus,
        giStatusDesc: soData.giStatusDesc,
        reserveStatus: soData.reserveStatus,
        invoiceStatus: soData.invoiceStatus,
        invoiceStatusDesc: soData.invoiceStatusDesc,
        orderDate: soData.orderDate,
        expDeliveryDate: soData.expDeliveryDate,
        salesID: soData.salesID,
        salesName: soData.salesName,
        discountID: soData.discountID,
        discountName: soData.discount.discountName,
        customerID: soData.customerID,
        customerName: soData.customer.customerName,
        soBillingAddress: soData.soBillingAddress,
        soDeliveryAddress: soData.soDeliveryAddress,
        currencyID: soData.currencyID,
        currencyCode: soData.currency.currencyCode,
        exchangeRate: soData.exchangeRate,
        subTotal: soData.subTotal,
        discountPercent: soData.discountPercent,
        discountAmount: soData.discountAmount,
        taxPercent: soData.taxPercent,
        taxAmount: soData.taxAmount,
        addCostTotal: soData.addCostTotal,
        grandTotal: soData.grandTotal,
        localAmount: soData.localAmount,
        note: soData.note,
        toc: soData.toc,
        approvalStatus: soData.approvalStatus,
        approvalStatusDesc: soData.approvalStatusDesc,
        salesOrderItem: [],
        salesOrderAddCost: soData.salesOrderAddCost,
        CreatedAt: soData.CreatedAt,
        UpdatedAt: soData.UpdatedAt,
        createdBy: soData.createdBy,
        updatedBy: soData.updatedBy,
      };
      soData.salesOrderItem.forEach((soItem) => {
        soValues.salesOrderItem.push({
          ID: soItem.ID,
          childItem:
            soItem.discountFreeItemID > 0 || soItem.itemPackageDetailID > 0
              ? true
              : false,
          discountFreeItemID: soItem.discountFreeItemID,
          discountAmountOffItemID: soItem.discountAmountOffItemID,
          giStatus: soItem.giStatus,
          reserveStatus: soItem.reserveStatus,
          itemID: soItem.itemID,
          itemType: soItem.itemType,
          itemTypeName: soItem.itemTypeName,
          itemCode:
            soItem.itemID === 0 && soItem.itemPackageID > 0
              ? soItem.itemPackage.packageCode
              : soItem.itemCode,
          itemName:
            soItem.itemID === 0 && soItem.itemPackageID > 0
              ? soItem.itemPackage.packageName
              : soItem.itemName,
          varCode: soItem.varCode,
          itemPackageID: soItem.itemPackageID,
          itemPackageDetailID: soItem.itemPackageDetailID,
          qty: soItem.qty,
          oriQty: soItem.itemPackageDetail.qty,
          currencyID: soItem.item.currencyID,
          oriMinimumPrice: soItem.item.minimumPrice,
          minimumPrice: soItem.item.minimumPrice * soItem.qty,
          uomID: soItem.uomID,
          uomName: soItem.uomName,
          unitPrice: soItem.unitPrice,
          discountAmountType: 1,
          discountPercent: soItem.discountPercent,
          discountAmount: soItem.discountAmount,
          taxPercent: soItem.taxPercent,
          taxAmount: soItem.taxAmount,
          taxName: soItem.taxName,
          totalPrice: soItem.totalPrice,
          editable: false,
          deleteInd: false,
        });
      });
      setSo(soValues);
      handleChangeCustomer(soData.customerID);
    }
  }
}

export default SalesOrderForm;
